/**
 * The Application Identifier table — General Specifications 26.0 §3, and the
 * primary-key/qualifier tables of Digital Link URI Syntax 1.7.0 §4.
 *
 * Declarative on purpose. Every entry is a row of a printed table, so it can be
 * diffed against the specification by eye; the behaviour lives in
 * `format.ts` and `check-digit.ts`, which are each written once.
 *
 * ## Validation leans permissive, deliberately
 *
 * Where this table is uncertain it errs toward accepting. The asymmetry is not
 * laziness — it follows from what the resolver is for. A false 400 on a code
 * already printed onto packaging is unrecoverable and violates invariant 1
 * ("never 404 a printed barcode" has the same force for 400). A false accept
 * merely resolves an identifier that GS1 would have called malformed. One of
 * those failure modes can be fixed by a later deploy; the other has already
 * shipped on a carton.
 *
 * Places where this table is knowingly loose are marked with the exact token
 * `LOOSE:` — greppable, colon included, so `grep -rn "LOOSE:" src` is a
 * complete and current list of what is not yet nailed down. Resolved ones lose
 * the token entirely rather than being annotated as former ones, because a
 * marker that matches resolved entries is worse than no marker: it makes the
 * list unreadable at a glance and trains people to skim it.
 */
import { parseFormat } from "./format.js";
const define = (d) => d;
/**
 * Key qualifiers. None of these may open a path; each is only legal following
 * the primary key that admits it.
 */
export const QUALIFIER_AIS = {
    "10": define({ ai: "10", label: "BATCH/LOT", notation: "X..20" }),
    "21": define({ ai: "21", label: "SERIAL", notation: "X..20" }),
    "22": define({ ai: "22", label: "CPV", notation: "X..20" }),
    "254": define({ ai: "254", label: "GLN EXTENSION COMPONENT", notation: "X..20" }),
    "8011": define({ ai: "8011", label: "CPID SERIAL", notation: "N..12" }),
    "8019": define({ ai: "8019", label: "SRIN", notation: "N..10" }),
};
/**
 * Primary keys, keyed by AI. **This set is closed by GS1's resolver conformance
 * suite** — its `plausibleDlURINoAlphas` regex, whose fifteen keys these are,
 * exactly. A path opening with anything else is not one we resolve.
 *
 * **The standard's set is sixteen, and the difference is deliberate.** Digital
 * Link URI Syntax 1.7.0 §4.3 also lists `415` (Pay To), reinstated in release
 * 1.3.0; §4.9 even defines a path for it. We follow the suite because the suite
 * is what conformance is measured by here, and 415 identifies a payee in an
 * invoicing flow rather than anything that reaches packaging — so the gap costs
 * nothing in practice. It is a gap nonetheless, and calling the set "closed by
 * the standard" was how it stayed invisible from c8 to c77.
 *
 * Recorded at c77b as divergence `ai-415-not-a-primary-key`, ruling `suite-wins`.
 * The general posture — suite over text, where they conflict — is argued in the
 * vault's `Decisions/` note of 2026-08-17 and stands **proposed** there, pending
 * Cameron: the behaviour is settled and long-standing, the precedent is not.
 */
export const PRIMARY_KEY_AIS = {
    "00": define({
        ai: "00",
        label: "SSCC",
        notation: "N18",
        checkDigitLength: 18,
        qualifiers: [],
    }),
    "01": define({
        ai: "01",
        label: "GTIN",
        notation: "N14",
        checkDigitLength: 14,
        qualifiers: ["22", "10", "21"],
    }),
    "253": define({
        ai: "253",
        label: "GDTI",
        notation: "N13,X..17?",
        checkDigitLength: 13,
        qualifiers: [],
    }),
    "255": define({
        ai: "255",
        label: "GCN",
        notation: "N13,N..12?",
        checkDigitLength: 13,
        qualifiers: [],
    }),
    "401": define({ ai: "401", label: "GINC", notation: "X..30", qualifiers: [] }),
    "402": define({
        ai: "402",
        label: "GSIN",
        notation: "N17",
        checkDigitLength: 17,
        qualifiers: [],
    }),
    "414": define({
        ai: "414",
        label: "GLN",
        notation: "N13",
        checkDigitLength: 13,
        qualifiers: ["254"],
    }),
    "417": define({
        ai: "417",
        label: "PARTY GLN",
        notation: "N13",
        checkDigitLength: 13,
        qualifiers: [],
    }),
    "8003": define({
        ai: "8003",
        label: "GRAI",
        notation: "N14,X..16?",
        checkDigitLength: 14,
        qualifiers: [],
    }),
    "8004": define({ ai: "8004", label: "GIAI", notation: "X..30", qualifiers: [] }),
    "8006": define({
        ai: "8006",
        label: "ITIP",
        // Modelled as three components rather than a flat N18, which accepts the
        // same 18 digits but says what they are: a GTIN-14, then piece number, then
        // total pieces. The decomposition makes checkDigitLength 14 self-evidently
        // "the GTIN part" instead of a bare number needing a comment to justify it.
        //
        // Only the first 14 digits carry the check digit — running it over all 18
        // rejects every valid ITIP. Corroborated by the oracle, which declares the
        // same position (see divergences.ts, `check-digit-verification-method`).
        notation: "N14,N2,N2",
        checkDigitLength: 14,
        qualifiers: ["22", "10", "21"],
    }),
    "8010": define({
        ai: "8010",
        label: "CPID",
        // Was knowingly loose (validated as CSET 82) until the oracle settled the
        // exact set: CSET 39 is `#-/0123456789A-Z` — uppercase only, and exactly
        // three punctuation characters. Note it is NOT "alphanumerics plus some
        // punctuation"; a guess would have been plausible and wrong, which is why
        // it stayed loose rather than being approximated.
        // See test/oracle/divergences.ts, ruling `cpid-charset-39`.
        notation: "Y..30",
        qualifiers: ["8011"],
    }),
    "8013": define({
        ai: "8013",
        label: "GMN",
        // LOOSE: and the oracle cannot close it — checked in c4 and recorded.
        // GMN terminates in a check-character *pair* computed over CSET 82 with a
        // different algorithm from the mod-10 check digit (GenSpecs §7.9.5). The
        // GS1 toolkit does not implement it either: its AI 8013 entry carries no
        // checkDigit field at all. So this must be closed from the specification
        // text or not at all, and nobody should re-run that investigation hoping
        // otherwise. Declaring no checkDigitLength stays correct rather than merely
        // incomplete — mod-10 over a GMN would be wrong, not approximate.
        // See test/oracle/divergences.ts, rulings `gmn-check-character-pair` and
        // `gmn-type-and-length`.
        notation: "X..25",
        qualifiers: [],
    }),
    "8017": define({
        ai: "8017",
        label: "GSRNP",
        notation: "N18",
        checkDigitLength: 18,
        qualifiers: ["8019"],
    }),
    "8018": define({
        ai: "8018",
        label: "GSRN",
        notation: "N18",
        checkDigitLength: 18,
        qualifiers: ["8019"],
    }),
};
/** Every AI this package can validate in a path, primary keys and qualifiers. */
export const PATH_AIS = {
    ...QUALIFIER_AIS,
    ...PRIMARY_KEY_AIS,
};
export const isPrimaryKeyAI = (ai) => Object.prototype.hasOwnProperty.call(PRIMARY_KEY_AIS, ai);
export const isQualifierAI = (ai) => Object.prototype.hasOwnProperty.call(QUALIFIER_AIS, ai);
// Formats are parsed once at module load rather than per request. The table is
// static, and the resolver's hot path parses a URI on every scan.
const FORMAT_CACHE = new Map();
for (const def of Object.values(PATH_AIS)) {
    FORMAT_CACHE.set(def.ai, parseFormat(def.notation));
}
/**
 * The parsed format for an AI, or `undefined` if this package does not know it.
 */
export const formatFor = (ai) => FORMAT_CACHE.get(ai);
//# sourceMappingURL=ai.js.map